import type { AppUser } from "@/types/user";
import {
  Permission,
  ROLE_TEMPLATES,
  USER_ROLE_ASSIGNMENTS,
} from "../types/permissions";

// Map forecasterRole title → role template ID
// Used everywhere we need to resolve a user's permissions from their role title
export const ROLE_TITLE_TO_TEMPLATE_ID: Record<string, string> = {
  "Accounts Assistant": "preparer",
  "Finance Manager": "manager",
  "Regional Finance Director": "regional",
  "Regional Manager": "regional",
  "Chief Financial Officer": "cfo",
  CFO: "cfo",
  "Chief Executive Officer": "executive",
  CEO: "executive",
  "External Board Member": "external",
  "External User": "external",
  "System Administrator": "system_admin",
  User: "preparer", // generic fallback
};

/**
 * Resolve role template ID from a user's forecasterRole title
 */
export function getRoleTemplateId(forecasterRole: string): string {
  return ROLE_TITLE_TO_TEMPLATE_ID[forecasterRole] ?? "preparer";
}

/**
 * Get the role template for a given user
 */
export function getRoleForUser(user: AppUser) {
  const roleId = getRoleTemplateId(user.forecasterRole);
  return ROLE_TEMPLATES.find((r) => r.id === roleId) || null;
}

/**
 * Check if a user has a specific permission
 */
export function hasPermission(user: AppUser, permission: Permission): boolean {
  const role = getRoleForUser(user);
  if (!role) return false;
  return role.permissions.includes(permission);
}

/**
 * Check if a user has any of the specified permissions
 */
export function hasAnyPermission(
  user: AppUser,
  permissions: Permission[],
): boolean {
  return permissions.some((p) => hasPermission(user, p));
}

/**
 * Check if a user has all of the specified permissions
 */
export function hasAllPermissions(
  user: AppUser,
  permissions: Permission[],
): boolean {
  return permissions.every((p) => hasPermission(user, p));
}

/**
 * Get entity restriction for a user
 */
export function getEntityRestriction(user: AppUser) {
  const role = getRoleForUser(user);
  if (!role) return { type: "all" as const, entities: [] };

  const userAssignment = USER_ROLE_ASSIGNMENTS.find(
    (u) => u.userId === user.id,
  );

  return {
    type: role.entityRestriction,
    entities: role.allowedEntities || [],
    assignedEntity: userAssignment?.entity,
  };
}
